/**
 * Layout Primitives
 *
 * Building blocks for arranging content:
 * - Column: Vertical stack
 * - Row: Horizontal stack
 * - Grid: Multi-column grid
 * - Card: Contained section with optional header
 * - Accordion: Collapsible section
 * - Section: Titled section
 */

import React, { useState } from 'react';

export function Column({ gap = 2, align, padding, children, style = {} }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: `var(--space-${gap})`,
        alignItems: align,
        padding: padding ? `var(--space-${padding})` : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Row({ gap = 2, align = 'center', justify, wrap, padding, children, style = {} }) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        gap: `var(--space-${gap})`,
        alignItems: align,
        justifyContent: justify,
        flexWrap: wrap ? 'wrap' : undefined,
        padding: padding ? `var(--space-${padding})` : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Grid({ columns = 2, gap = 2, minWidth, padding, children, style = {} }) {
  const gridTemplateColumns = minWidth
    ? `repeat(auto-fit, minmax(${minWidth}, 1fr))`
    : `repeat(${columns}, 1fr)`;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns,
        gap: `var(--space-${gap})`,
        padding: padding ? `var(--space-${padding})` : undefined,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Card({
  title,
  subtitle,
  icon,
  variant = 'default', // default, outlined, elevated, colored
  color, // primary, success, warning, error
  padding = 3,
  children,
  style = {},
}) {
  const variants = {
    default: {
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      boxShadow: 'none',
    },
    outlined: {
      background: 'transparent',
      border: '1px solid var(--color-border)',
      boxShadow: 'none',
    },
    elevated: {
      background: 'var(--color-surface)',
      border: 'none',
      boxShadow: 'var(--shadow-md)',
    },
    colored: {
      background: color ? `rgba(var(--color-${color}-rgb), 0.1)` : 'var(--color-surface)',
      border: color ? `1px solid rgba(var(--color-${color}-rgb), 0.2)` : '1px solid var(--color-border)',
      boxShadow: 'none',
    },
  };

  const variantStyles = variants[variant] || variants.default;

  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        padding: `var(--space-${padding})`,
        ...variantStyles,
        ...style,
      }}
    >
      {(title || subtitle || icon) && (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 'var(--space-2)',
            marginBottom: children ? 'var(--space-3)' : 0,
          }}
        >
          {icon && <span style={{ fontSize: '20px' }}>{icon}</span>}
          <div style={{ flex: 1 }}>
            {title && (
              <div
                style={{
                  fontWeight: 'var(--font-semibold)',
                  fontSize: 'var(--text-sm)',
                  color: color ? `var(--color-${color})` : 'var(--color-text)',
                }}
              >
                {title}
              </div>
            )}
            {subtitle && (
              <div
                style={{
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-muted)',
                  marginTop: '2px',
                }}
              >
                {subtitle}
              </div>
            )}
          </div>
        </div>
      )}
      {children}
    </div>
  );
}

export function Accordion({ title, icon, defaultOpen = false, children }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div
      style={{
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-3)',
          background: 'var(--color-surface)',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-medium)',
          color: 'var(--color-text)',
          textAlign: 'left',
        }}
      >
        {icon && <span>{icon}</span>}
        <span style={{ flex: 1 }}>{title}</span>
        <span
          style={{
            transition: 'transform 0.2s',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          ▼
        </span>
      </button>
      {isOpen && (
        <div
          style={{
            padding: 'var(--space-3)',
            borderTop: '1px solid var(--color-border)',
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

export function Section({ title, subtitle, icon, children }) {
  return (
    <div style={{ marginBottom: 'var(--space-4)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-2)',
        }}
      >
        {icon && <span style={{ fontSize: '16px' }}>{icon}</span>}
        <div>
          <div
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-semibold)',
              color: 'var(--color-text)',
            }}
          >
            {title}
          </div>
          {subtitle && (
            <div
              style={{
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-muted)',
              }}
            >
              {subtitle}
            </div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
